/**
 * D3 Chart Export Utility
 * 
 * Exports D3.js SVG charts to PNG/JPEG images for inclusion in PDF/Word reports.
 * Properly inlines CSS styles for correct rendering.
 */

/**
 * Clone SVG and inline all computed styles
 * This is necessary because SVG-to-canvas doesn't preserve external CSS
 */
function cloneSvgWithInlineStyles(svg: SVGSVGElement): SVGSVGElement {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  
  // Get all elements in both original and clone
  const originalElements = svg.querySelectorAll('*');
  const clonedElements = clone.querySelectorAll('*');
  
  // Copy computed styles to clone
  originalElements.forEach((origEl, i) => {
    const cloneEl = clonedElements[i] as SVGElement | HTMLElement;
    if (!cloneEl) return;
    
    const computedStyle = window.getComputedStyle(origEl);
    
    // Copy important style properties
    const importantProps = [
      'fill', 'stroke', 'stroke-width', 'stroke-dasharray', 'opacity',
      'font-family', 'font-size', 'font-weight', 'text-anchor',
      'dominant-baseline', 'alignment-baseline'
    ];
    
    importantProps.forEach(prop => {
      const value = computedStyle.getPropertyValue(prop);
      if (value && value !== 'none' && value !== '') {
        (cloneEl as SVGElement).style.setProperty(prop, value);
      }
    });
  });
  
  // Set explicit dimensions
  const bbox = svg.getBBox();
  const width = svg.getAttribute('width') || svg.clientWidth || bbox.width + bbox.x;
  const height = svg.getAttribute('height') || svg.clientHeight || bbox.height + bbox.y;
  
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  
  return clone;
}

/**
 * Find the main chart SVG (not icon SVGs)
 * Icons are typically small (< 50px) and inside buttons/headers
 */
function findChartSvg(container: HTMLElement): SVGSVGElement | null {
  const allSvgs = container.querySelectorAll('svg');
  
  for (const svg of Array.from(allSvgs)) {
    const width = svg.getAttribute('width') || svg.clientWidth;
    const height = svg.getAttribute('height') || svg.clientHeight;
    
    // Chart SVGs are typically larger than 100px
    // Icons are typically 16-24px
    const numWidth = typeof width === 'string' ? parseFloat(width) : width;
    const numHeight = typeof height === 'string' ? parseFloat(height) : height;
    
    if (numWidth > 100 && numHeight > 100) {
      return svg as SVGSVGElement;
    }
  }
  
  // Fallback: return the largest SVG
  let largestSvg: SVGSVGElement | null = null;
  let largestArea = 0;
  
  for (const svg of Array.from(allSvgs)) {
    const rect = svg.getBoundingClientRect();
    const area = rect.width * rect.height;
    if (area > largestArea) {
      largestArea = area;
      largestSvg = svg as SVGSVGElement;
    }
  }
  
  return largestSvg;
}

/**
 * Export a D3 chart element to a PNG data URL
 * @param chartElement - DOM element containing the SVG chart
 * @param options - Export options (scale, format, background)
 * @returns Promise<string> - Data URL of the exported image
 */
export async function exportChartToImage(
  chartElement: HTMLElement,
  options: {
    scale?: number;
    format?: 'png' | 'jpeg';
    backgroundColor?: string;
  } = {}
): Promise<string> {
  const {
    scale = 2,
    format = 'png',
    backgroundColor = '#ffffff',
  } = options;

  // Find the CHART SVG (not icon SVGs)
  const svg = findChartSvg(chartElement);
  if (!svg) {
    throw new Error('No chart SVG element found in container');
  }

  console.log(`Found chart SVG: ${svg.getAttribute('width')}x${svg.getAttribute('height')}`);

  // Clone SVG with inlined styles
  const clonedSvg = cloneSvgWithInlineStyles(svg);
  
  // Get dimensions
  const width = parseFloat(clonedSvg.getAttribute('width') || '700');
  const height = parseFloat(clonedSvg.getAttribute('height') || '350');
  
  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Could not create canvas context');
  }

  // Set background
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.scale(scale, scale);

  // Serialize SVG to data URL
  const svgString = new XMLSerializer().serializeToString(clonedSvg);
  const svgBase64 = btoa(unescape(encodeURIComponent(svgString)));
  const dataUrl = `data:image/svg+xml;base64,${svgBase64}`;

  // Load SVG into image and draw to canvas
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        ctx.drawImage(img, 0, 0, width, height);
        const outputUrl = canvas.toDataURL(`image/${format}`, 0.95);
        resolve(outputUrl);
      } catch (err) {
        reject(new Error('Failed to draw image to canvas'));
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load SVG as image'));
    };
    
    img.src = dataUrl;
  });
}

/**
 * Download a chart as an image file
 */
export async function downloadChartAsImage(
  chartElement: HTMLElement,
  filename: string = 'chart',
  format: 'png' | 'jpeg' = 'png'
): Promise<void> {
  const dataUrl = await exportChartToImage(chartElement, { format });
  
  const link = document.createElement('a');
  link.download = `${filename}.${format}`;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Export all D3 charts on the page to images
 */
export async function exportAllCharts(): Promise<Array<{id: string; dataUrl: string; name: string}>> {
  const charts = [
    { id: 'monte-carlo-d3-chart', name: 'Monte_Carlo_Distribution' },
    { id: 'expense-breakdown-d3-chart', name: 'Expense_Breakdown' },
    { id: 'loss-timeline-d3-chart', name: 'Loss_Timeline' },
    { id: 'revenue-comparison-d3-chart', name: 'Revenue_Comparison' },
  ];

  const results: Array<{id: string; dataUrl: string; name: string}> = [];

  for (const chart of charts) {
    const element = document.querySelector(`[data-testid="${chart.id}"]`) as HTMLElement;
    if (element) {
      try {
        const dataUrl = await exportChartToImage(element, { scale: 2 });
        results.push({ id: chart.id, dataUrl, name: chart.name });
        console.log(`✅ Exported ${chart.name}`);
      } catch (error) {
        console.warn(`Failed to export ${chart.name}:`, error);
      }
    }
  }

  return results;
}

/**
 * Convert data URL to Blob for API upload
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

/**
 * Upload exported charts to backend for PDF/Word inclusion
 */
export async function uploadChartsToBackend(
  charts: Array<{id: string; dataUrl: string; name: string}>,
  reportId: string,
  apiUrl: string
): Promise<{success: boolean; chartUrls: Record<string, string>; uploadedCount: number}> {
  const chartUrls: Record<string, string> = {};
  let uploadedCount = 0;
  
  for (const chart of charts) {
    try {
      const blob = dataUrlToBlob(chart.dataUrl);
      const formData = new FormData();
      formData.append('file', blob, `${chart.name}.png`);
      formData.append('report_id', reportId);
      formData.append('chart_name', chart.name);
      
      // Use the new chart-upload endpoint
      const response = await fetch(`${apiUrl}/api/v1/chart-upload/upload`, {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        chartUrls[chart.name] = data.chart_id;
        uploadedCount++;
        console.log(`✅ Uploaded chart: ${chart.name}`);
      } else {
        console.warn(`Failed to upload ${chart.name}: ${response.status}`);
      }
    } catch (error) {
      console.error(`Failed to upload ${chart.name}:`, error);
    }
  }
  
  return { success: uploadedCount > 0, chartUrls, uploadedCount };
}
